<template>
  <div data-component-group="filter-forms" data-component="shop">
    <FiltersForm
      :has-filters="hasFilters"
      :is-skeleton-loading="isSkeletonLoading"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <MerchantDropdown
        class="autocomplete__one-line"
        v-model="selectedMerchants"
        prepend-inner-icon="store"
        :label="$t('merchants')"
        :countries="selectedCountries"
        :hide-details="true"
        multiple
      />

      <v-autocomplete
        clearable
        v-model="category"
        :items="categories"
        prepend-inner-icon="category"
        :label="$t('category')"
        :loading="isLoadingCategory"
        :return-object="true"
        @update:search="categorySearch"
        :item-title="getCategoryNameNCountry"
      />

      <base-date-picker
        :date="dates"
        :placeholder="$t('dates_placeholder')"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        item-title="name"
        item-value="id"
        label="Status"
        @focus="onStatusFocus"
      />
    </FiltersForm>
  </div>
</template>

<script>
import FiltersForm from './FiltersBase';
import { isNum, isNonEmptyArr, isNonEmptyStr, isStr, debounce, isObj } from '@/utils';
import { v1 as uuidv1 } from 'uuid';
import MerchantDropdown from '@/components/dropdowns/Merchant.vue';

const categorySearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

export default {
  components: {
    MerchantDropdown,
    FiltersForm,
  },
  props: {
    isSkeletonLoading: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['filter', 'clearFilters'],
  data() {
    return {
      category: null,
      dates: null,
      formattedDates: [],
      dateConfig: {
        mode: 'range',
      },
      search: null,
      status: null,
      selectedCountries: [],
      selectedMerchants: [],
      isLoadingCategory: false,
    };
  },
  computed: {
    hasFilters() {
      const { search, status, selectedCountries, selectedMerchants, dates, category } = this;

      return isNonEmptyStr(search) ||
        isNum(status) ||
        isNum(category?.id) ||
        isNonEmptyArr(selectedCountries) ||
        isNonEmptyArr(selectedMerchants) ||
        !!dates;
    },
    statuses() {
      return this.$store.getters['sponsorships/statuses'];
    },
    categories: {
      get() {
        const storeCategories = this.$store.getters['pepper/groups'] ?? [];
        if (isObj(this.category) && storeCategories.findIndex(c => c.id === this.category.id) === -1) {
          return [...storeCategories, this.category];
        }
        return storeCategories;
      },
      set(newCategories) {
        return newCategories;
      },
    },
  },
  methods: {
    getCategoryNameNCountry(category) {
      return category ? `${category.name} (${category?.country.code})` : '';
    },
    categorySearch(nextSearch) {
      if (isStr(nextSearch)) {
        if (this.isLoadingCategory) {
          this.isLoadingCategory = false;
          return false;
        }

        if (isNonEmptyStr(nextSearch)) {
          categorySearchDebouncer(() => this.getCategory(nextSearch));
        }
      }
    },
    getCategory(search) {
      const { selectedCountries } = this;
      const query = {
        countries: selectedCountries,
        search,
      };

      this.isLoadingCategory = true;

      return this.$store
        .dispatch('pepper/getGroups', query)
        .then(({ groups }) => {
          this.categories = groups;
          this.isLoadingCategory = false;
          return groups;
        })
        .catch((err) => {
          this.isLoadingCategory = false;
          return err;
        });
    },
    onChangeDate(dates) {
      this.dates = dates;
      this.formattedDates = dates.split(' - ');
    },
    getStatuses() {
      return this.$store.dispatch('sponsorships/getStatuses');
    },
    onFilter() {
      const { selectedCountries, selectedMerchants, search, status, dates, formattedDates, category } = this;

      this.$emit('filter', {
        search,
        status,
        countries: selectedCountries,
        merchants: selectedMerchants.map(m => m.id),
        categories: category ? [category.id] : null,
        dates: dates ? formattedDates : null,
      });
    },
    onClearFilters() {
      this.search = null;
      this.status = null;
      this.selectedCountries = [];
      this.selectedMerchants = [];
      this.dates = null;
      this.formattedDates = [];

      this.$emit('clearFilters');
    },
    onStatusFocus() {
      if (this.statuses.length === 0) {
        this.getStatuses();
      }
    },
  },
};
</script>
